import { useState } from 'react';
import { ApiError } from '@/lib/api';
import { submitReview } from '@/lib/review-api';
import { Button } from '@/components/ui/button';

interface ReviewModalProps {
  bookingId: string;
  accessToken: string;
  onDone: () => void;
  onClose: () => void;
}

function StarPicker({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-1" role="group" aria-label="Rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          aria-label={`${star} star${star !== 1 ? 's' : ''}`}
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className="text-3xl leading-none focus:outline-none cursor-pointer"
        >
          <span className={(hovered || value) >= star ? 'text-amber-400' : 'text-slate-300'}>
            ★
          </span>
        </button>
      ))}
    </div>
  );
}

export function ReviewModal({ bookingId, accessToken, onDone, onClose }: ReviewModalProps) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    if (rating === 0) return;
    setBusy(true);
    setError(null);
    try {
      await submitReview(accessToken, {
        bookingId,
        rating,
        comment: comment.trim() || null,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to submit review');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Leave a review"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
    >
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-slate-800">Leave a review</h2>
        <p className="mt-1 text-sm text-slate-500">How was your class?</p>

        <div className="mt-5">
          <StarPicker value={rating} onChange={setRating} />
        </div>

        <div className="mt-4">
          <label htmlFor="review-comment" className="block text-sm font-medium text-slate-700">
            Comment <span className="font-normal text-slate-400">(optional)</span>
          </label>
          <textarea
            id="review-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={500}
            rows={4}
            placeholder="Share your experience…"
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <p className="mt-0.5 text-right text-xs text-slate-400">{comment.length}/500</p>
        </div>

        {error && (
          <p role="alert" className="mt-3 text-sm text-red-600">{error}</p>
        )}

        <div className="mt-5 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={busy || rating === 0}>
            {busy ? 'Submitting…' : 'Submit review'}
          </Button>
        </div>
      </div>
    </div>
  );
}
